import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Card, Table } from '@/components_deprecated'
import { format, parseISO } from 'date-fns'
import { enforceHourMinuteString } from '@/modules/activities'
import { Row } from '@/modules/activities/components/overview/ActivitiesWeekTableRow'
import { CheckmarkDiv } from '@/modules/activities/components/overview/CheckmarkDiv'
import styled from 'styled-components'
import { useUser } from '@/utils/hooks'
import { ApproveCommentModal } from '../ApproveCommentModal'
import type { CalendarItemNode, WorkPeriodNode } from '../types.graphql'
import { WorkPeriodDetail } from '../WorkPeriodDetail'
import { DietButton } from '../components/DietButton'
import { H3 } from '@/components'
import { AddonButton } from '@/modules/activities/components/overview/AddonCell'
import type { SalaryModuleConfiguration } from '../hooks/useSalaryModuleConfiguration'
import { SalaryDayButton } from '../components/SalaryDayButton'

const WorkPeriodContainer = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1rem 0;
  width: 100%;

  & > h3 {
    margin: 0;
  }

  & > div > span {
    opacity: 0;
  }
  &:hover {
    & > div > span {
      opacity: 1;
    }
  }
`

const WorkPeriodWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;

  p {
    font-weight: 600;
  }

  ${props => props.theme.media.mobile} {
    width: 100%;
    max-width: 100%;
  }
`

const ActionWrapper = styled.div`
  color: ${props => props.theme.colors.gray1};
  background-color: ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  font-size: 18px;
  display: flex;
  justify-content: space-between;
  overflow: hidden;

  & > div {
    width: 100%;
    padding: 0.5rem 1rem;
  }
  & > div:hover {
    background-color: ${props => props.theme.colors.gray7};
    cursor: pointer;
  }
`

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0;
  gap: 0.5rem;

  ${props => props.theme.media.mobile} {
    width: 100%;
    flex-direction: column;
    width: 100%;
  }
`

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
`

const StyledTable = styled(Table)`
  margin-left: -1rem;

  tr {
    cursor: default;
  }
`

const StyledRow = styled(Row)`
  height: 14px;
`

const WorkPeriodTitle = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;

  & > h3 {
    margin: 0;
  }

  ${props => props.theme.media.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`

const ApproveWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  width: 100%;
`

const ApproveColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 50%;
  max-width: 50%;
  background: ${props => props.theme.colors.gray10};
  border-radius: 16px;
`

const ApproveBox = styled.div`
  display: flex;
  gap: 0.25rem;
  padding: 0.5rem;
  height: 4rem;
  align-items: center;
  cursor: pointer;
`

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background: ${props => props.theme.colors.gray8};
`

const ApproveBoxText = styled.p`
  padding: 0.5rem 0.5rem 1rem 0.5rem;
  margin: 0;
`

const TableAndApproveWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 1rem;

  ${props => props.theme.media.mobile} {
    flex-direction: column;
    gap: 0.5rem;
  }
`

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  ${props => props.theme.media.mobile} {
    justify-content: flex-start;
    h3 {
      font-size: 1rem;
    }
  }
`

const TextAndAction = styled.div<{ border?: boolean }>`
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  border-radius: ${props => props.theme.standardBorderRadius};
  border: ${props =>
    props.border ? `1px solid ${props.theme.colors.gray9}` : 'none'};
  background-color: ${props => props.theme.colors.white};

  & > *:first-child {
    border-right: 1px solid ${props => props.theme.colors.gray9};
    margin-right: 1rem;
    padding-right: 1rem;
  }
  & > *:last-child {
    cursor: pointer;
  }
  ${props => props.theme.media.mobile} {
    width: 100%;
  }
`

export const WorkPeriodDetails: React.FC<{
  workPeriod: WorkPeriodNode
  userId: string
  salaryCalendarSettings: SalaryModuleConfiguration

  patchWorkPeriod: (
    workPeriodId: string,
    values: Partial<
      Pick<
        WorkPeriodNode,
        | 'approvedByUser'
        | 'approvedByManager'
        | 'userComment'
        | 'managerComment'
      >
    > &
      Partial<{ calendarItem: string | null }>
  ) => void
  onClose?: () => void
}> = ({
  workPeriod,
  userId,
  salaryCalendarSettings,

  patchWorkPeriod,
  onClose,
}) => {
  const addPrompt = usePrompt()

  const me = useUser()

  const translations = useTranslate({
    duration: 'common.duration',
    seeWork: 'common.see-work',
    overtime: 'common.overtime',
    totalWork: 'common.total-work',
    field: 'common.field',
    amount: 'common.amount',
    workPeriod: 'common.work-period',
    paidPause: 'activities.paid-pause',
    salaryCode: 'common.salary-code',
    approvedByDriver: 'common.approved-by-driver',
    approvedByManager: 'common.approved-by-manager',

    noCalendarItemConnected: 'salary.no-calendar-item-connected',

    department: 'common.department',
    facility: 'common.facility',
    addons: 'salary.addons',
  })
  async function handleApproveWorkPeriodDriver(
    workPeriodId: string,
    approved: boolean,
    existingComment: string
  ) {
    if (approved) {
      const { data } = await addPrompt<{ comment: string } | null>(resolve => (
        <ApproveCommentModal
          existingComment={existingComment}
          existingApproved={approved}
          onSubmit={resolve}
        />
      ))
      if (!data) return
      patchWorkPeriod(workPeriodId, {
        approvedByUser: approved,
        userComment: data.comment,
      })
    } else {
      patchWorkPeriod(workPeriodId, {
        approvedByUser: approved,
      })
    }
  }

  async function handleApproveWorkPeriodManager(
    workPeriodId: string,
    approved: boolean,
    existingComment: string
  ) {
    if (approved) {
      const { data } = await addPrompt<{ comment: string } | null>(resolve => (
        <ApproveCommentModal
          existingComment={existingComment}
          existingApproved={approved}
          onSubmit={resolve}
        />
      ))
      if (!data) return
      patchWorkPeriod(workPeriodId, {
        approvedByManager: approved,
        managerComment: data.comment,
      })
    } else {
      patchWorkPeriod(workPeriodId, {
        approvedByManager: approved,
      })
    }
  }

  const isManager = me.id !== workPeriod.user.id

  async function handleShowWorkPeriod(
    workPeriod: WorkPeriodNode,
    calendarItem?: CalendarItemNode | null
  ) {
    const registrationNumbers: string[] = []
    if (calendarItem?.vehicle?.registrationNumber) {
      registrationNumbers.push(calendarItem.vehicle.registrationNumber)
    }
    if (calendarItem?.secondaryVehicle?.registrationNumber) {
      registrationNumbers.push(calendarItem.secondaryVehicle.registrationNumber)
    }
    await addPrompt<{ id: string } | null>(resolve => (
      <WorkPeriodDetail workPeriodId={workPeriod.id} onSubmit={resolve} />
    ))
  }

  const workPeriodStart = parseISO(workPeriod.datetimeStart)
  const workPeriodEnd = parseISO(workPeriod.datetimeEnd)

  const displayDateDays = `${format(workPeriodStart, 'd MMM HH:mm')} - ${format(
    workPeriodEnd,
    'd MMM HH:mm'
  )}`

  const title = `${translations.workPeriod}: ${displayDateDays}`

  const showApproveWrapper =
    salaryCalendarSettings.driverApprove ||
    salaryCalendarSettings.managerApprove

  return (
    <FlexWrapper>
      {onClose && (
        <TitleWrapper>
          <FlexRow>
            <ActionWrapper>
              <div onClick={() => onClose()}>
                <Icon icon="arrow-left" onClick={() => onClose()} />
              </div>
            </ActionWrapper>
            <ActionWrapper>
              <div onClick={() => handleShowWorkPeriod(workPeriod)}>
                <Icon
                  icon="clock"
                  onClick={() => handleShowWorkPeriod(workPeriod)}
                />
              </div>
            </ActionWrapper>
            <H3>{title}</H3>
          </FlexRow>
          <WorkPeriodTitle>
            <SalaryDayButton
              salaryDay={workPeriod.salaryDay}
              workPeriod={workPeriod}
              userId={userId}
              canEdit={true}
              border={true}
            />
            <DietButton
              workPeriod={workPeriod}
              userId={userId}
              canEdit={true}
              border={true}
            />
            <TextAndAction border={true} onClick={() => {}}>
              <div>{translations.addons}</div>
              <AddonButton
                userId={userId}
                datetimeStart={workPeriodStart}
                datetimeEnd={workPeriodEnd}
                addonsAsText={['']}
              />
            </TextAndAction>
          </WorkPeriodTitle>
        </TitleWrapper>
      )}

      <WorkPeriodWrapper>
        <WorkPeriodContainer key={workPeriod.id}>
          {!onClose && (
            <>
              <TitleWrapper>
                <ActionWrapper>
                  <div onClick={() => handleShowWorkPeriod(workPeriod)}>
                    <Icon
                      icon="clock"
                      onClick={() => handleShowWorkPeriod(workPeriod)}
                    />
                  </div>
                </ActionWrapper>
                <H3>{title}</H3>
              </TitleWrapper>
              <WorkPeriodTitle>
                <DietButton
                  workPeriod={workPeriod}
                  userId={userId}
                  canEdit={true}
                  border={true}
                />
                <TextAndAction border={true} onClick={() => {}}>
                  <div>{translations.addons}</div>
                  <AddonButton
                    userId={userId}
                    datetimeStart={workPeriodStart}
                    datetimeEnd={workPeriodEnd}
                    addonsAsText={['']}
                  />
                </TextAndAction>
              </WorkPeriodTitle>
            </>
          )}
          <TableAndApproveWrapper>
            <StyledTable
              noBorders
              noShadow
              headerMargin
              rowHoverBackground="gray10"
            >
              <thead>
                <tr>
                  <th>{translations.salaryCode}</th>
                  <th>{translations.amount}</th>
                </tr>
              </thead>
              <tbody>
                {workPeriod.groupedActivitySums.map(activitySum => (
                  <StyledRow
                    key={activitySum.id}
                    isActive={true}
                    isAbsence={false}
                    onClick={() => {}}
                  >
                    <td>{activitySum?.name}</td>
                    <td>{enforceHourMinuteString(activitySum?.sum)}</td>
                  </StyledRow>
                ))}
              </tbody>
            </StyledTable>
            {showApproveWrapper && (
              <ApproveWrapper>
                {salaryCalendarSettings.driverApprove && (
                  <ApproveColumn>
                    <ApproveBox
                      onClick={() => {
                        void handleApproveWorkPeriodDriver(
                          workPeriod.id,
                          !workPeriod.approvedByUser,
                          workPeriod.userComment
                        )
                      }}
                    >
                      {translations.approvedByDriver}
                      <CheckmarkDiv
                        checked={workPeriod.approvedByUser}
                        onClick={evt => {
                          evt.preventDefault()
                          evt.stopPropagation()
                          void handleApproveWorkPeriodDriver(
                            workPeriod.id,
                            !workPeriod.approvedByUser,
                            workPeriod.userComment
                          )
                        }}
                      />
                    </ApproveBox>
                    <Separator />
                    <ApproveBoxText>{workPeriod.userComment}</ApproveBoxText>
                  </ApproveColumn>
                )}
                {salaryCalendarSettings.managerApprove && (
                  <ApproveColumn>
                    <ApproveBox
                      onClick={() =>
                        isManager
                          ? handleApproveWorkPeriodManager(
                              workPeriod.id,
                              !workPeriod.approvedByManager,
                              workPeriod.managerComment
                            )
                          : null
                      }
                    >
                      {translations.approvedByManager}
                      <CheckmarkDiv
                        checked={workPeriod.approvedByManager}
                        onClick={evt => {
                          if (!isManager) return
                          evt.preventDefault()
                          evt.stopPropagation()
                          void handleApproveWorkPeriodManager(
                            workPeriod.id,
                            !workPeriod.approvedByManager,
                            workPeriod.managerComment
                          )
                        }}
                      />
                    </ApproveBox>
                    <Separator />
                    <ApproveBoxText>{workPeriod.managerComment}</ApproveBoxText>
                  </ApproveColumn>
                )}
              </ApproveWrapper>
            )}
          </TableAndApproveWrapper>
        </WorkPeriodContainer>
      </WorkPeriodWrapper>
    </FlexWrapper>
  )
}
